import { useState } from 'react';

import FileSaver from 'file-saver';

import { ReferentielEnumDtoReferentialEnumCSVEnum } from 'lib_api/lib/api/gen';

import { supportedFilesDetails } from 'components/WrappedComponents/Uploader/constants';
import { SupportedFile } from 'types/enums/SupportedFile';
import { ReferentielEnum } from 'types/Referentiel';
import { RequestStatus, RequestStatusByName } from 'types/RequestStatus';

import { useApi } from '../ApiStoreContext';
import { useHandleBackErrors } from '../utils/handleBackErrors';

type ExportFunction<T extends ReferentielEnum> = (
  referentiel: T,
  name: string,
) => Promise<void>;

interface HookResult {
  status: RequestStatusByName;
  downloadCsv: ExportFunction<ReferentielEnumDtoReferentialEnumCSVEnum>;
}

function useDownloadReferentiel(): HookResult {
  const api = useApi().ReferentielControllerApi;
  const behaviourOnError = useHandleBackErrors();
  const [requestStatus, setRequetStatus] = useState<RequestStatusByName>({});

  const updateStatus = (referentiel: string, newStatus: RequestStatus) => {
    setRequetStatus((prevState: RequestStatusByName) => {
      return {
        ...prevState,
        [referentiel]: newStatus,
      };
    });
  };

  function download<T extends ReferentielEnum>(
    extension: string,
    downloadFunction: (referentiel: T) => Promise<Response>,
  ) {
    return async (referentiel: T, name: string) => {
      await downloadFunction(referentiel)
        .then((result: Response) => {
          // Create dom link to download file
          return result.blob();
        })
        .then((blob: Blob) => {
          FileSaver.saveAs(blob, name + extension);
          updateStatus(name, RequestStatus.SUCCESS);
        })
        .catch((errorResponse: Response) => {
          updateStatus(name, RequestStatus.ERROR);
          behaviourOnError(errorResponse, true);
        });
    };
  }

  const downloadCsv = download<ReferentielEnumDtoReferentialEnumCSVEnum>(
    supportedFilesDetails[SupportedFile.CSV].extension,
    (referentiel: ReferentielEnumDtoReferentialEnumCSVEnum) =>
      api.exportCSVReferentielUsingGET(referentiel),
  );

  return {
    status: requestStatus,
    downloadCsv: downloadCsv,
  };
}

export default useDownloadReferentiel;
